import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Download, Loader2, Search, Smartphone } from 'lucide-react';
import DateRangePicker from '@/components/ui/DateRangePicker';
import PaginationWrapper from '@/components/ui/PaginationWrapper';

const formatDate = (d) => format(d, 'yyyy-MM-dd');
const formatDisplay = (d) => format(d, 'MM/dd/yyyy');
const PAGE_SIZE = 20;

const FILTER_ROW_KEYS = [
    'userName',
    'email',
    'userRole',
    'wholesaler',
    'apiEndPoint',
    'browserUrl',
    'userIPAddress',
    'requestdate',
];

function rowMatchesSearch(row, queryLower) {
    return FILTER_ROW_KEYS.some((key) => {
        const v = row[key];
        if (v == null || v === '') return false;
        if (key === 'requestdate') {
            const d = new Date(v);
            const formatted = isNaN(d.getTime()) ? String(v) : format(d, 'MM/dd/yyyy HH:mm');
            return `${String(v)} ${formatted}`.toLowerCase().includes(queryLower);
        }
        return String(v).toLowerCase().includes(queryLower);
    });
}

function DashboardUsageAnalyticsPopup({ closePopup }) {
    const today = new Date();
    const yesterday = subDays(today, 1);

    const [startDate, setStartDate] = useState(yesterday);
    const [endDate, setEndDate] = useState(today);
    const [displayStart, setDisplayStart] = useState(formatDisplay(yesterday));
    const [displayEnd, setDisplayEnd] = useState(formatDisplay(today));
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [activePage, setActivePage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const fetchData = useCallback(async (start, end) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/admin/GetDashboard_Usage_Analytics', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href,
                },
                body: JSON.stringify({
                    StartDate: formatDate(start),
                    EndDate: formatDate(end),
                    UserRole: 'Admin',
                }),
            });
            if (!response.ok) throw new Error('Request failed');
            const json = await response.json();
            setData(Array.isArray(json) ? json : []);
            setActivePage(1);
        } catch {
            setError('Failed to load data');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(startDate, endDate);
    }, []);

    const handleCalendar = useCallback((e, picker) => {
        const start = picker.startDate;
        const end = picker.endDate;
        setStartDate(start);
        setEndDate(end);
        setDisplayStart(formatDisplay(start));
        setDisplayEnd(formatDisplay(end));
        fetchData(start, end);
    }, [fetchData]);

    const handleOpenChange = useCallback((open) => {
        if (!open) closePopup();
    }, [closePopup]);

    const filteredData = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return data;
        return data.filter((row) => rowMatchesSearch(row, q));
    }, [data, searchQuery]);

    useEffect(() => {
        setActivePage(1);
    }, [searchQuery]);

    const handleExportCSV = useCallback(() => {
        if (!filteredData.length) return;
        const headers = ['User Name', 'Email', 'Role', 'Wholesaler', 'API Endpoint', 'Browser URL', 'IP Address', 'Request Date'];
        const keys = ['userName', 'email', 'userRole', 'wholesaler', 'apiEndPoint', 'browserUrl', 'userIPAddress', 'requestdate'];
        const rows = filteredData.map(row =>
            keys.map(k => {
                const val = row[k] ?? '';
                return `"${String(val).replace(/"/g, '""')}"`;
            }).join(',')
        );
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `Dashboard_Usage_Analytics_${formatDate(startDate)}_${formatDate(endDate)}.csv`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filteredData, startDate, endDate]);

    const minDate = new Date(2016, 0, 1);

    const formatCellValue = (key, value) => {
        if (!value) return '—';
        if (key === 'requestdate') {
            const d = new Date(value);
            return isNaN(d) ? value : format(d, 'MM/dd/yyyy HH:mm');
        }
        return value;
    };

    const columns = [
        { key: 'userName',      label: 'User Name',   className: 'w-[10%]' },
        { key: 'email',         label: 'Email',        className: 'w-[15%]', tooltip: true },
        { key: 'userRole',      label: 'Role',         className: 'w-[10%]', full: true },
        { key: 'wholesaler',    label: 'Wholesaler',   className: 'w-[14%]',  tooltip: true },
        { key: 'apiEndPoint',   label: 'API Endpoint', className: 'w-[14%]', tooltip: true },
        { key: 'browserUrl',    label: 'Browser URL',  className: 'w-[16%]', tooltip: true },
        { key: 'userIPAddress', label: 'IP Address',   className: 'w-[10%]' },
        { key: 'requestdate',   label: 'Request Date', className: 'w-[15%]' },
    ];

    const paginatedData = filteredData.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-5xl h-[calc(100vh-3rem)] [&>div]:!p-0">
                <div className="flex flex-col h-full px-4 sm:px-6">
                    <DialogHeader className="flex-shrink-0 pt-4 sm:pt-6">
                        <DialogTitle>Dashboard Usage Analytics</DialogTitle>
                    </DialogHeader>

                    {isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Toolbar */}
                            <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 mt-4">
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                                <div className={loading ? 'pointer-events-none opacity-50' : ''}>
                                <DateRangePicker
                                    className="w-[320px] max-w-full h-10"
                                    maxRangeDays={7}
                                    popoverClassName="[&_[data-slot=calendar]]:!min-w-0 [&_[data-slot=calendar]]:!max-w-full [&_[data-slot=calendar]]:w-auto"
                                    calendarClassName="p-1 [--cell-size:2.1rem]"
                                    calendarClassNames={{
                                        months: "flex flex-col gap-1 md:flex-row",
                                        month: "flex w-full flex-col gap-1",
                                        nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 h-7 px-1",
                                        dropdowns: "flex h-7 w-full items-center justify-center gap-1.5 text-xs font-semibold",
                                        dropdown_root: "relative rounded-md border bg-card px-1.5 py-0.5 min-w-[70px] cursor-pointer hover:bg-accent transition-colors h-6 flex items-center text-[11px]",
                                        caption_label: "select-none font-medium [&>svg]:text-muted-foreground flex h-6 items-center gap-1 rounded-md pl-1.5 pr-0.5 text-[11px] [&>svg]:size-3",
                                        month_caption: "flex h-7 w-full items-center justify-center px-7",
                                        weekdays: "flex",
                                        weekday: "text-muted-foreground w-[2.1rem] select-none text-xs font-medium py-1 flex items-center justify-center",
                                        day: "w-[2.1rem] h-[2.1rem] aspect-square p-0.5 text-center select-none group/day relative font-medium text-xs",
                                        button_previous: "h-4 w-4 select-none p-0 aria-disabled:hidden hover:bg-accent [&>svg]:size-4 mt-2 rounded-none",
                                        button_next: "h-4 w-4 select-none p-0 aria-disabled:opacity-50 hover:bg-accent [&>svg]:size-4 mt-2 rounded-none ms-auto",
                                    }}
                                    initialSettings={{
                                        startDate: startDate,
                                        endDate: endDate,
                                        minDate: minDate,
                                        maxDate: today,
                                        locale: { format: 'MM/DD/YYYY' },
                                    }}
                                    onApply={handleCalendar}
                                >
                                    <div className="flex items-center gap-0 w-[320px] max-w-full rounded-md overflow-hidden border border-input bg-card hover:shadow-sm transition-shadow h-10">
                                        <div className="flex items-center gap-1 px-3 flex-1">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
                                            <Input
                                                type="text"
                                                className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                                value={displayStart}
                                                readOnly
                                            />
                                        </div>
                                        <div className="h-6 w-px bg-border flex-shrink-0" />
                                        <div className="flex items-center gap-1 px-3 flex-1">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
                                            <Input
                                                type="text"
                                                className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                                value={displayEnd}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </DateRangePicker>
                                </div>

                                <div className="relative w-full min-w-[200px] max-w-sm sm:max-w-md">
                                    <Search
                                        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none"
                                        aria-hidden
                                    />
                                    <Input
                                        type="search"
                                        placeholder="Search…"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="h-10 pl-9"
                                        autoComplete="off"
                                        aria-label="Filter table by keyword"
                                    />
                                </div>
                                </div>

                                {data.length > 0 && (
                                    <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={handleExportCSV}
                                                    className="h-10 w-10 cursor-pointer"
                                                >
                                                    <Download className="h-5 w-5" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Download CSV</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>

                            {/* Table area */}
                            {loading ? (
                                <div className="flex-1 flex justify-center items-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : data.length > 0 ? (
                                filteredData.length > 0 ? (
                                <>
                                    <div className="flex-1 overflow-y-auto min-h-0 mt-4 rounded-md border">
                                        <TooltipProvider>
                                            <Table className="table-fixed" wrapperClassName="overflow-visible">
                                                <TableHeader>
                                                    <TableRow>
                                                        {columns.map((col) => (
                                                            <TableHead key={col.key} className={col.className}>
                                                                {col.label}
                                                            </TableHead>
                                                        ))}
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {paginatedData.map((row, idx) => (
                                                        <TableRow key={idx}>
                                                            {columns.map((col) => {
                                                                const value = formatCellValue(col.key, row[col.key]);
                                                                if (col.tooltip && row[col.key]) {
                                                                    return (
                                                                        <TableCell key={col.key}>
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <span className="block truncate cursor-default">
                                                                                        {value}
                                                                                    </span>
                                                                                </TooltipTrigger>
                                                                                <TooltipContent side="top" className="max-w-xs break-all">
                                                                                    {row[col.key]}
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    );
                                                                }
                                                                return (
                                                                    <TableCell key={col.key}>
                                                                        {col.full ? value : (
                                                                            <span className="block truncate">{value}</span>
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TooltipProvider>
                                    </div>
                                    {filteredData.length > PAGE_SIZE && (
                                        <div className="flex-shrink-0 flex justify-center py-3">
                                            <PaginationWrapper
                                                activePage={activePage}
                                                itemsCountPerPage={PAGE_SIZE}
                                                totalItemsCount={filteredData.length}
                                                pageRangeDisplayed={5}
                                                onChange={setActivePage}
                                            />
                                        </div>
                                    )}
                                </>
                                ) : (
                                    <p className="flex-shrink-0 text-sm text-muted-foreground mt-4">
                                        No results match your search.
                                    </p>
                                )
                            ) : error ? (
                                <p className="flex-shrink-0 text-sm text-destructive mt-4">{error}</p>
                            ) : (
                                <div className="flex items-center justify-center flex-1 text-muted-foreground text-sm">
                                    No data available
                                </div>
                            )}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default DashboardUsageAnalyticsPopup;